import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { addFamilyContact } from '~entities/family/api/add-family-contact';
import { mapFamilyApiError } from '~entities/family/lib/map-family-api-error';
import {
  type CreateParentWithoutStripeParams,
  useCreateParentWithoutStripeMutation,
} from '~entities/parent/api/create-parent-without-stripe';

import { Address } from '~features/parents/ui/address';
import { ParentDetails } from '~features/parents/ui/parent-details';

import { KEYS } from '~shared/api/query-keys';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Form } from '~shared/shadcn/ui/form';
import type { ParentFormValues } from '~shared/types/schema';
import { parentFormSchema } from '~shared/types/schema';
import { zodResolverWithLogging } from '~shared/utils/zod-resolver-with-logging';

const defaultValues: Partial<ParentFormValues> = {
  firstName: '',
  lastName: '',
  email: '',
  primaryPhone: '',
  familyName: '',
  relation: 'Guardian',
  address: {
    street: '',
    city: '',
    state: '',
    zip: '',
    country: '',
  },
};

interface CreateAndLinkGuardianDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** GHL locationId — POST /parents resolves studio via findByLocationIdString. */
  locationId: string;
  familyId: string;
  studentId: string;
}

export const CreateAndLinkGuardianDialog = ({
  open,
  onOpenChange,
  locationId,
  familyId,
  studentId,
}: CreateAndLinkGuardianDialogProps) => {
  const queryClient = useQueryClient();
  const createParent = useCreateParentWithoutStripeMutation();
  const form = useForm<ParentFormValues>({
    resolver: zodResolverWithLogging(parentFormSchema),
    defaultValues,
  });
  const isPending = createParent.isPending || form.formState.isSubmitting;

  useEffect(() => {
    if (!open) {
      form.reset(defaultValues);
      form.clearErrors();
    }
  }, [open, form]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (isPending) return;
    onOpenChange(nextOpen);
  };

  const onSubmit = async (data: ParentFormValues) => {
    try {
      const created = await createParent.mutateAsync({
        ...data,
        contactId: '',
        studioId: locationId,
        familyName: (() => {
          const named = data.familyName?.trim();
          return named && named.length > 0 ? named : data.lastName;
        })(),
        registration: true,
        isCreatedFromStudio: true,
      } as CreateParentWithoutStripeParams);

      const parentId = created._id;
      if (!parentId) {
        toast.error(
          'Guardian was created, but we could not link them. Refresh and search by name.',
        );
        handleOpenChange(false);
        return;
      }

      await addFamilyContact({
        familyId,
        parentId,
        isBillingContact: false,
        communicationLevel: 'Full',
        portalAccessEnabled: true,
      });

      await queryClient.invalidateQueries({
        queryKey: [KEYS.FAMILY_BY_STUDENT, studentId],
      });

      toast.success(
        `${data.firstName} ${data.lastName} created and linked to this family`,
      );
      handleOpenChange(false);
    } catch (error) {
      toast.error(
        mapFamilyApiError(
          error,
          'Could not create and link this guardian. Check the details and try again.',
        ),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='max-h-[90vh] w-[calc(100vw-2rem)] overflow-y-auto p-4 sm:max-w-3xl sm:p-8'>
        <DialogHeader>
          <DialogTitle className='text-center text-xl'>
            Create and link guardian
          </DialogTitle>
          <DialogDescription className='text-center'>
            Creates a new Parent login in this studio and links them to this
            family with portal access on.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-8'>
            <ParentDetails form={form} />
            <Address form={form} />

            <div className='flex flex-col-reverse gap-2 sm:flex-row sm:justify-end'>
              <Button
                type='button'
                variant='outline'
                disabled={isPending}
                onClick={() => {
                  handleOpenChange(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type='submit'
                disabled={isPending}
                className='sm:min-w-48'
              >
                {isPending ? (
                  <>
                    <Loader2 className='mr-2 size-4 animate-spin' />
                    Creating and linking…
                  </>
                ) : (
                  'Create and link'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
